// Worker page section: dead jobs grouped by (kind, error_fingerprint), with
// one-at-a-time requeue for requeue-safe kinds (P2-H02, P2-H03). Keys and the
// truncated last_error only: payloads are never selected or shown (P2-H04).
import { useState } from 'react';
import { Badge } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@heyroot/ui/primitives/Table';
import { PERMS } from '../../../../rbac/permissions';
import {
  requeueWorkerDeadJob,
  useWorkerAction,
  useWorkerDeadGroups,
  useWorkerDeadJobs,
  type WorkerDeadGroupView,
} from '../../../lib/worker-api';
import type { WorkerSectionWeb } from '../sections';
import { ActionOutcome, QueryState, SectionHeading, fmtTs, useCanOperate } from '../worker-ui';

function errorText(name: string | null, message: string | null): string {
  if (!name && !message) return '—';
  return [name, message].filter(Boolean).join(': ');
}

function DeadJobRows({ group }: { group: WorkerDeadGroupView }) {
  const q = useWorkerDeadJobs(group.kind, group.errorFingerprint);
  const canOperate = useCanOperate();
  const [success, setSuccess] = useState<string | null>(null);
  const requeue = useWorkerAction((id: string) => requeueWorkerDeadJob(id));
  const jobs = q.data?.jobs ?? [];
  return (
    <div className="flex flex-col gap-2" aria-label={`Dead jobs of ${group.kind}`}>
      <ActionOutcome error={requeue.error} success={requeue.error ? null : success} />
      <QueryState
        isPending={q.isPending}
        isError={q.isError}
        isEmpty={jobs.length === 0}
        emptyTitle="No dead jobs in this group"
        onRetry={() => void q.refetch()}
      >
        <Table dense>
          <TableHead>
            <tr>
              <TableHeaderCell>Job</TableHeaderCell>
              <TableHeaderCell>Tenant</TableHeaderCell>
              <TableHeaderCell>Attempts</TableHeaderCell>
              <TableHeaderCell>Dedupe key</TableHeaderCell>
              <TableHeaderCell>Finished</TableHeaderCell>
              <TableHeaderCell>Last error</TableHeaderCell>
              {canOperate && <TableHeaderCell>Actions</TableHeaderCell>}
            </tr>
          </TableHead>
          <TableBody>
            {jobs.map((j) => (
              <TableRow key={j.id}>
                <TableCell>{j.id}</TableCell>
                <TableCell>{j.tenantId ?? '—'}</TableCell>
                <TableCell>
                  {j.attempts} / {j.maxAttempts}
                </TableCell>
                <TableCell>{j.dedupeKey ?? '—'}</TableCell>
                <TableCell>{fmtTs(j.finishedAt)}</TableCell>
                <TableCell>{errorText(j.lastErrorName, j.lastErrorMessage)}</TableCell>
                {canOperate && (
                  <TableCell>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={requeue.isPending || !j.requeueSafe}
                      title={
                        j.requeueSafe
                          ? 'Requeue this job'
                          : 'Not requeue-safe: requeue is refused for this kind'
                      }
                      onClick={() =>
                        requeue.mutate([j.id], {
                          onSuccess: (res) => setSuccess(`Requeue ${j.id}: ${res.result}`),
                        })
                      }
                    >
                      Requeue
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </QueryState>
    </div>
  );
}

function DeadJobsSection() {
  const q = useWorkerDeadGroups();
  const [open, setOpen] = useState<WorkerDeadGroupView | null>(null);
  const groups = q.data?.groups ?? [];
  const groupId = (g: WorkerDeadGroupView) => `${g.kind}\u0000${g.errorFingerprint ?? ''}`;
  return (
    <section className="flex flex-col gap-3" aria-label="Dead jobs">
      <SectionHeading
        title="Dead jobs"
        description="Grouped by kind and error fingerprint. Requeue is one job at a time, and only for requeue-safe kinds."
      />
      <QueryState
        isPending={q.isPending}
        isError={q.isError}
        isEmpty={groups.length === 0}
        emptyTitle="No dead jobs"
        onRetry={() => void q.refetch()}
      >
        <div className="overflow-x-auto">
          <Table dense>
            <TableHead>
              <tr>
                <TableHeaderCell>Kind</TableHeaderCell>
                <TableHeaderCell>Fingerprint</TableHeaderCell>
                <TableHeaderCell>Dead</TableHeaderCell>
                <TableHeaderCell>Last dead</TableHeaderCell>
                <TableHeaderCell>Last error</TableHeaderCell>
                <TableHeaderCell>Requeue</TableHeaderCell>
                <TableHeaderCell />
              </tr>
            </TableHead>
            <TableBody>
              {groups.map((g) => (
                <TableRow key={groupId(g)}>
                  <TableCell>{g.kind}</TableCell>
                  <TableCell>{g.errorFingerprint ?? '—'}</TableCell>
                  <TableCell>{g.deadCount}</TableCell>
                  <TableCell>{fmtTs(g.lastDeadAt)}</TableCell>
                  <TableCell>{errorText(g.lastErrorName, g.lastErrorMessage)}</TableCell>
                  <TableCell>
                    {g.requeueSafe ? (
                      <Badge variant="success">requeue-safe</Badge>
                    ) : (
                      <Badge variant="neutral">not requeue-safe</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="link"
                      aria-expanded={open !== null && groupId(open) === groupId(g)}
                      onClick={() =>
                        setOpen(open !== null && groupId(open) === groupId(g) ? null : g)
                      }
                    >
                      {open !== null && groupId(open) === groupId(g) ? 'Hide jobs' : 'Show jobs'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {open && <DeadJobRows key={groupId(open)} group={open} />}
      </QueryState>
    </section>
  );
}

export const section: WorkerSectionWeb = {
  key: 'dead-jobs',
  order: 20,
  label: 'Dead jobs',
  perm: PERMS.WORKER_READ,
  Component: DeadJobsSection,
};
